import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  Typography, 
  Paper, 
  Avatar, 
  Container,
  AppBar,
  Toolbar,
  InputAdornment
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';

const ChatBot = () => {
  const [messages, setMessages] = useState([
    { text: "Full width enabled. Height set to 80vh. Ready for input!", sender: 'bot' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    setMessages((prev) => [...prev, { text: inputValue, sender: 'user' }]);
    setInputValue('');

    // Simulated Bot Response
    setTimeout(() => {
      setMessages((prev) => [
        ...prev, 
        { text: "I'm responding in this wide-format view!", sender: 'bot' }
      ]);
    }, 600);
  };

  return (
    <Container 
      maxWidth={false} 
      disableGutters 
      sx={{ 
        height: '85vh', // Back to your preferred height
        display: 'flex', 
        flexDirection: 'column', 
        bgcolor: '#f8f9fa',
        borderBottom: '1px solid #ddd' 
      }}
    >
      {/* Header */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            <SmartToyIcon />
          </Avatar>
          <Typography variant="h6" fontWeight="bold">AI Workspace</Typography>
        </Toolbar>
      </AppBar>

      {/* Message Area */}
      <Box 
        ref={scrollRef}
        sx={{ 
          flexGrow: 1, 
          overflowY: 'auto', 
          p: { xs: 2, md: 5 }, // Generous horizontal padding for wide screens
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        {messages.map((msg, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Avatar 
              sx={{ 
                bgcolor: msg.sender === 'user' ? 'secondary.main' : 'grey.300',
                color: msg.sender === 'user' ? 'white' : 'black'
              }}
            >
              {msg.sender === 'user' ? <PersonIcon /> : <SmartToyIcon />}
            </Avatar>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                px: 3,
                maxWidth: '60%', // Keeping bubbles from stretching TOO far
                borderRadius: 4,
                bgcolor: msg.sender === 'user' ? 'primary.main' : '#fff',
                color: msg.sender === 'user' ? 'white' : 'text.primary',
                border: msg.sender === 'user' ? 'none' : '1px solid #e0e0e0',
                boxShadow: '0px 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <Typography variant="body1">{msg.text}</Typography>
            </Paper>
          </Box>
        ))}
      </Box>

      {/* Full-Width Input Footer */}
      <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #eee' }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <TextField
            fullWidth
            placeholder="Write a message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            variant="outlined"
            sx={{ 
              '& .MuiOutlinedInput-root': { borderRadius: 3 }, 
              "padding":0
            }}
          />
          <IconButton 
            onClick={handleSend} 
            color="primary" 
            disabled={!inputValue.trim()}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: '#eee' }
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Container>
  );
};

export default ChatBot;